import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  XCircle,
  CheckCheck,
  PenLine,
  LifeBuoy,
  MoreHorizontal,
  Receipt,
  ShoppingBag,
  Store,
  CalendarDays,
  ClipboardList,
  Users,
} from "lucide-react";
import toast from "react-hot-toast";
import { isApiClientError } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { bookingService } from "@/services/bookingService";
import { activityService } from "@/services/activityService";
import {
  ActionMenu,
  ActionMenuItem,
  Button,
  Card,
  ConfirmModal,
  EmptyState,
} from "@/components/ui";
import {
  BookingActionCallout,
  BookingDetailAmount,
  BookingDetailHeader,
  type BookingDetailHeaderMeta,
  BookingDetailInfo,
  BookingAdminDetailView,
  BookingHandshakePanel,
  BookingAdminTimeline,
  BookingPartnerHandshakeSection,
  BookingPartnerSummaryCard,
  BookingSellerNotesModal,
  BookingSellerNotesSection,
  BookingStatusBadge,
  ReceiptDisplay,
  ReceiptFlowModals,
  ReceiptUploadButton,
  mapBookingToListRow,
  bookingPartyCount,
  getPartnerVisitDateTimeLabels,
  showPartnerReceiptUpload,
  showPartnerManualReceiptAmount,
  showPartnerCancelBooking,
  needsCompletion,
  getUpfrontPaidCents,
  useReceiptFlow,
  canViewHandshake,
  canInteractWithHandshake,
} from "@/components/bookings";
import { PageFrame, ErrorState, SectionCard } from "@/components/common";
import type { Activity, Booking } from "@/types";
import { Permission, UserRole } from "@/types";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/lib/money";
import { toastApiError } from "@/lib/errorMessages";
import type { AdminBookingDetailResponse } from "@/services/bookingService";
import { newSupportTicketPathWithSubject } from "@/pages/support/supportPaths";

export default function BookingDetailPage() {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("bookings");
  const { bookingId, sellerId: scopedSellerId } = useParams<{
    bookingId: string;
    sellerId?: string;
  }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    getCurrentUserId,
    isCustomer,
    hasPermission,
    user,
  } = useAuthStore();

  const sellerUserId = getCurrentUserId();
  const customerMode = isCustomer();
  const adminMode = hasPermission(Permission.BOOKINGS_READ_ANY);
  const adminPartnerScope = adminMode && !!scopedSellerId;
  const sellerMode = user?.role === UserRole.SELLER;
  const hostMode = user?.role === UserRole.HOST;
  const activityOwnerMode = sellerMode || hostMode;
  const bookingsListPath = adminPartnerScope
    ? `/sellers/${scopedSellerId}/bookings`
    : "/bookings";
  const listBackLink = {
    variant: "ghost" as const,
    fallback: bookingsListPath,
    children: t("detail.backToList"),
  };

  const handshakeViewer = { customerMode, adminMode, hasPermission };
  const showHandshake = canViewHandshake(handshakeViewer);
  const canInteractHandshake = canInteractWithHandshake(handshakeViewer);

  const canSendHandshakeMessage =
    !customerMode &&
    (adminMode
      ? hasPermission(Permission.HANDSHAKES_MESSAGE_SEND_ANY)
      : hasPermission(Permission.HANDSHAKES_MESSAGE_SEND));

  const receiptFlow = useReceiptFlow({ customerMode });
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancellingBooking, setIsCancellingBooking] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [isCompletingBooking, setIsCompletingBooking] = useState(false);
  const [sellerNotesEditOpen, setSellerNotesEditOpen] = useState(false);
  const [sellerNotesDraft, setSellerNotesDraft] = useState("");
  const [isSavingSellerNotes, setIsSavingSellerNotes] = useState(false);
  const [isAdjustingReceipt, setIsAdjustingReceipt] = useState(false);

  const activityOwnerUserId = adminPartnerScope
    ? scopedSellerId
    : sellerUserId;

  const { data: activities = [] } = useQuery<Activity[]>({
    queryKey: ["activities"],
    queryFn: () => activityService.getAll(),
    enabled: !!activityOwnerUserId && !customerMode && !adminMode,
  });

  const activityMap = useMemo(() => {
    const map = new Map<string, Activity>();
    activities.forEach((a) => map.set(a.id, a));
    return map;
  }, [activities]);

  const {
    data: booking,
    isLoading,
    error,
    refetch,
  } = useQuery<Booking>({
    queryKey: ["booking", bookingId],
    queryFn: () => {
      if (!bookingId) {
        throw new Error("bookingId required");
      }
      return bookingService.getById(bookingId);
    },
    enabled: !!bookingId && !adminMode,
  });

  const {
    data: adminDetail,
    isLoading: adminDetailLoading,
    error: adminDetailError,
    refetch: refetchAdminDetail,
  } = useQuery<AdminBookingDetailResponse>({
    queryKey: ["admin", "booking-detail", bookingId],
    queryFn: () => {
      if (!bookingId) {
        throw new Error("bookingId required");
      }
      return bookingService.getAdminDetail(bookingId);
    },
    enabled: !!bookingId && adminMode,
  });

  const adminRow = useMemo(() => {
    if (!adminDetail) return null;
    return mapBookingToListRow(adminDetail.booking, {
      customerMode: false,
      activityRequiresHandshake: adminDetail.activity.requires_handshake,
    });
  }, [adminDetail]);

  const row = useMemo(() => {
    if (!booking) return null;
    return mapBookingToListRow(booking, { activityMap, customerMode });
  }, [booking, activityMap, customerMode]);

  const canUpdatePartnerBooking =
    (activityOwnerMode && hasPermission(Permission.BOOKINGS_UPDATE)) ||
    (adminMode && hasPermission(Permission.BOOKINGS_UPDATE_ANY));
  const canCancelPartnerBooking =
    (activityOwnerMode && hasPermission(Permission.BOOKINGS_CANCEL)) ||
    (adminMode && hasPermission(Permission.BOOKINGS_CANCEL_ANY));

  const handleCancelBooking = async () => {
    if (!bookingId) return;

    setIsCancellingBooking(true);
    try {
      await bookingService.cancelBooking(bookingId);
      toast.success(t("detail.cancelBookingSuccess"));
      setShowCancelConfirm(false);
      void queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
    } catch (e) {
      if (isApiClientError(e) && e.response?.status === 409) {
        toast.error(t("detail.cancelBookingPastVisitError"));
      } else {
        toastApiError(e, "generic", t("detail.cancelBookingError"));
      }
      console.error(e);
    } finally {
      setIsCancellingBooking(false);
    }
  };

  const handleCompleteBooking = async () => {
    if (!bookingId) return;

    setIsCompletingBooking(true);
    try {
      await bookingService.completeBooking(bookingId);
      toast.success(t("detail.markCompleteSuccess"));
      setShowCompleteConfirm(false);
      void queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
    } catch (e) {
      toastApiError(e, "generic", t("detail.markCompleteError"));
      console.error(e);
    } finally {
      setIsCompletingBooking(false);
    }
  };

  const handleEditSellerNotes = (
    _bookingId: string,
    currentNotes: string | null,
  ) => {
    setSellerNotesDraft(currentNotes ?? "");
    setSellerNotesEditOpen(true);
  };

  const handleSaveSellerNotes = async (bookingId: string, sellerNotes: string) => {
    setIsSavingSellerNotes(true);
    try {
      await bookingService.updateSellerNotes(bookingId, sellerNotes);
      toast.success(t("sellerNotesModal.saveSuccess"));
      setSellerNotesEditOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
    } catch (e) {
      toastApiError(e, "generic", t("sellerNotesModal.saveError"));
      console.error(e);
    } finally {
      setIsSavingSellerNotes(false);
    }
  };

  const canAdjustReceiptTotal =
    adminMode &&
    hasPermission(Permission.ECONOMIC_EVENTS_WRITE) &&
    adminRow?.isReceiptBased === true &&
    adminRow.receiptTotalCents != null;

  const handleAdminReceiptAdjust = async (
    newTotalCents: number,
    reason: string | null,
  ) => {
    if (!bookingId) return;

    setIsAdjustingReceipt(true);
    try {
      await bookingService.adjustAdminReceiptTotal(
        bookingId,
        newTotalCents,
        reason,
      );
      toast.success(
        t("adminReceiptAdjust.success", {
          total: formatMoney(newTotalCents),
        }),
      );
      void queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "booking-detail", bookingId],
      });
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "economic-events"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "settlements"] });
    } catch (e) {
      toastApiError(e, "generic", t("adminReceiptAdjust.error"));
      console.error(e);
      throw e;
    } finally {
      setIsAdjustingReceipt(false);
    }
  };

  if (!bookingId) {
    return <ErrorState message={t("detail.notFound")} />;
  }

  if (adminMode) {
    if (adminDetailLoading) {
      return (
        <PageFrame
          backLink={listBackLink}
          isLoading
          loadingMessage={t("detail.loading")}
        />
      );
    }

    if (adminDetailError || !adminDetail || !adminRow) {
      return (
        <PageFrame
          backLink={listBackLink}
          error
          errorMessage={t("detail.loadError")}
          onRetry={() => void refetchAdminDetail()}
        />
      );
    }

    return (
      <PageFrame
        backLink={{ ...listBackLink, className: "-ml-2 w-fit" }}
        className="gap-4 pb-6 sm:gap-5"
      >
        <BookingAdminDetailView
          row={adminRow}
          detail={adminDetail}
          showHandshake={showHandshake}
          canAdjustReceiptTotal={canAdjustReceiptTotal}
          isAdjustingReceipt={isAdjustingReceipt}
          onAdjustReceipt={handleAdminReceiptAdjust}
        />
      </PageFrame>
    );
  }

  if (isLoading) {
    return (
      <PageFrame
        backLink={listBackLink}
        isLoading
        loadingMessage={t("detail.loading")}
      />
    );
  }

  if (error || !booking || !row) {
    return (
      <PageFrame
        backLink={listBackLink}
        error
        errorMessage={t("detail.loadError")}
        onRetry={() => void refetch()}
      />
    );
  }

  const isProcessingReceipt =
    receiptFlow.processingByBookingId[row.id] ?? false;
  const fileInputResetKey = receiptFlow.fileInputResetKey[row.id] ?? 0;
  const showHandshakeSection = row.activityRequiresHandshake && showHandshake;
  const hasHandshakeChat = Boolean(
    showHandshakeSection && row.handshakeSessionId,
  );

  const showReceiptUploadAction =
    activityOwnerMode && showPartnerReceiptUpload(row);
  const showManualReceiptAmountAction =
    activityOwnerMode && showPartnerManualReceiptAmount(row);
  const showCancelBooking =
    canCancelPartnerBooking && showPartnerCancelBooking(row);
  const showCompleteBooking =
    canUpdatePartnerBooking && needsCompletion(row);
  const partyCount = bookingPartyCount(row);

  const servicesSubtitle =
    row.lineItems.length > 0
      ? row.lineItems
          .map((item) => `${item.quantity}× ${item.name}`)
          .join(" · ")
      : row.activityName;

  const reachedAccepted =
    row.status === "CANCELED" &&
    (row.receiptTotalCents != null ||
      Boolean(booking.partner_receipt_url) ||
      booking.external_payment_id != null);

  const sellerNotesSection = (
    <BookingSellerNotesSection
      notes={booking.seller_notes}
      onEdit={
        canUpdatePartnerBooking
          ? () => handleEditSellerNotes(row.id, booking.seller_notes ?? null)
          : undefined
      }
      className="w-full"
    />
  );

  const partnerModals = (
    <>
      <ReceiptFlowModals flow={receiptFlow} />

      <ConfirmModal
        closeLabel={tCommon("close")}
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => void handleCancelBooking()}
        title={t("detail.cancelBookingConfirmTitle")}
        message={
          adminMode
            ? t("detail.cancelBookingConfirmMessageAdmin", {
                name: row.customerDisplayName || "—",
                amount:
                  row.amountPaidCents != null
                    ? formatMoney(row.amountPaidCents)
                    : row.totalPriceCents != null
                      ? formatMoney(row.totalPriceCents)
                      : "—",
              })
            : t("detail.cancelBookingConfirmMessage")
        }
        confirmText={t("detail.cancelBookingConfirmAction")}
        cancelText={t("common:cancel")}
        isDestructive
        isLoading={isCancellingBooking}
      />

      <ConfirmModal
        closeLabel={tCommon("close")}
        isOpen={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={() => void handleCompleteBooking()}
        title={t("manageModal.completeConfirmTitle")}
        message={t("manageModal.completeConfirmMessage")}
        confirmText={t("detail.markComplete")}
        cancelText={t("common:cancel")}
        isLoading={isCompletingBooking}
      />

      <BookingSellerNotesModal
        isOpen={sellerNotesEditOpen}
        bookingId={bookingId}
        notes={sellerNotesDraft}
        isSaving={isSavingSellerNotes}
        onNotesChange={setSellerNotesDraft}
        onClose={() => {
          if (!isSavingSellerNotes) {
            setSellerNotesEditOpen(false);
          }
        }}
        onSave={(id, sellerNotes) => void handleSaveSellerNotes(id, sellerNotes)}
      />
    </>
  );

  if (activityOwnerMode) {
    const latestUploadPath = row.latestUploadPath;
    const receiptFooter =
      row.receiptTotalCents != null ? (
        <div className="border-t border-surface-200/70 pt-4">
          <ReceiptDisplay
            receiptTotalCents={row.receiptTotalCents}
            receiptUrl={row.receiptUrl}
          />
        </div>
      ) : null;

    const primaryActionBanner = showReceiptUploadAction ? (
      <BookingActionCallout
        tone="primary"
        icon={Receipt}
        title={t("detail.receiptSectionTitle")}
        description={t("detail.receiptSectionDescription")}
        action={
          <ReceiptUploadButton
            bookingId={row.id}
            isProcessing={isProcessingReceipt}
            fileInputResetKey={fileInputResetKey}
            fullWidth
            visitAt={row.visitAt}
            onFileSelect={(file) => void receiptFlow.uploadReceipt(row, file)}
          />
        }
      />
    ) : showManualReceiptAmountAction && latestUploadPath ? (
      <BookingActionCallout
        tone="warning"
        icon={PenLine}
        title={t("detail.receiptManualSectionTitle")}
        description={t("detail.receiptManualSectionDescription")}
        action={
          <Button
            variant="primary"
            className="w-full sm:w-auto"
            leftIcon={<PenLine className="h-4 w-4" />}
            onClick={() =>
              receiptFlow.openManualAmount(row.id, latestUploadPath)
            }
          >
            {t("receiptManualCta")}
          </Button>
        }
      />
    ) : showCompleteBooking ? (
      <BookingActionCallout
        tone="primary"
        icon={CheckCheck}
        title={t("detail.completeSectionTitle")}
        description={t("detail.completeSectionDescription")}
        action={
          <Button
            variant="primary"
            className="w-full sm:w-auto"
            leftIcon={<CheckCheck className="h-4 w-4" />}
            onClick={() => setShowCompleteConfirm(true)}
            disabled={isCompletingBooking || isProcessingReceipt}
          >
            {isCompletingBooking
              ? t("detail.markCompleteBusy")
              : t("detail.completeSectionAction")}
          </Button>
        }
      />
    ) : null;

    const upfrontPaidCents = getUpfrontPaidCents(row);
    const partnerAmount =
      row.receiptTotalCents != null
        ? { label: t("detail.receipt"), cents: row.receiptTotalCents }
        : upfrontPaidCents != null
          ? {
              label: row.isDeposit
                ? t("detail.depositPaid")
                : t("detail.amountPaid"),
              cents: upfrontPaidCents,
            }
          : null;

    const partnerHeaderAmount = partnerAmount ? (
      <BookingDetailAmount
        label={partnerAmount.label}
        value={formatMoney(partnerAmount.cents)}
      />
    ) : null;

    const partnerHeaderMenu = (
      <ActionMenu
        renderTrigger={(triggerProps) => (
          <Button
            {...triggerProps}
            variant="outline"
            size="sm"
            className="h-9 w-9 shrink-0 px-0"
            aria-label={t("detail.moreActions")}
            title={t("detail.moreActions")}
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden />
          </Button>
        )}
      >
        <ActionMenuItem
          icon={LifeBuoy}
          onSelect={() =>
            void navigate(
              newSupportTicketPathWithSubject(
                t("detail.supportTicketSubject", { id: row.id }),
              ),
            )
          }
        >
          {t("detail.supportHelpAction")}
        </ActionMenuItem>
        {showCancelBooking ? (
          <ActionMenuItem
            icon={XCircle}
            tone="danger"
            disabled={isCancellingBooking || isProcessingReceipt}
            onSelect={() => setShowCancelConfirm(true)}
          >
            {isCancellingBooking
              ? t("detail.cancelBookingBusy")
              : t("detail.cancelBookingConfirmAction")}
          </ActionMenuItem>
        ) : null}
      </ActionMenu>
    );

    const partnerVisit = getPartnerVisitDateTimeLabels(row);
    const partnerHeaderMeta: BookingDetailHeaderMeta[] = [
      ...(servicesSubtitle
        ? [
            {
              icon: row.lineItems.length > 0 ? ShoppingBag : Store,
              label: servicesSubtitle,
            },
          ]
        : []),
      ...(partnerVisit.shortDateLabel
        ? [
            {
              icon: CalendarDays,
              label: partnerVisit.timeLabel
                ? `${partnerVisit.shortDateLabel} · ${partnerVisit.timeLabel}`
                : partnerVisit.shortDateLabel,
            },
          ]
        : []),
      ...(partyCount > 0
        ? [{ icon: Users, label: t("guest", { count: partyCount }) }]
        : []),
    ];

    return (
      <PageFrame
        backLink={{ ...listBackLink, className: "-ml-2 w-fit" }}
        className="gap-4 pb-6 sm:gap-5"
      >
        <BookingDetailHeader
          className="shrink-0"
          title={row.customerDisplayName}
          customerName={row.customerDisplayName}
          badge={
            row.status !== "ABANDONED" ? (
              <BookingStatusBadge
                status={row.status}
                handshakeStatus={row.handshakeStatus}
                partnerView
                size="lg"
              />
            ) : null
          }
          meta={partnerHeaderMeta}
          actions={partnerHeaderAmount}
          menu={partnerHeaderMenu}
        >
          <BookingAdminTimeline
            status={row.status}
            requiresHandshake={row.activityRequiresHandshake}
            reachedAccepted={reachedAccepted}
            createdAt={row.createdAt}
            updatedAt={booking.updated_at}
          />
        </BookingDetailHeader>

        {primaryActionBanner}

        <div className="grid items-start gap-4 sm:gap-5 lg:grid-cols-5 lg:grid-rows-[auto_1fr]">
          <BookingPartnerSummaryCard
            row={row}
            booking={booking}
            footer={receiptFooter}
            className="min-w-0 lg:col-span-3"
          />

          {hasHandshakeChat && row.handshakeSessionId ? (
            <BookingPartnerHandshakeSection
              sessionId={row.handshakeSessionId}
              canInteract={canInteractHandshake}
              activityId={booking.activity_id}
              partySize={partyCount}
              className="min-w-0 lg:sticky lg:top-[calc(var(--content-sticky-top)+1rem)] lg:col-span-2 lg:row-span-2"
            />
          ) : null}

          <div
            className={cn(
              "min-w-0",
              hasHandshakeChat ? "lg:col-span-3" : "lg:col-span-2",
            )}
          >
            {sellerNotesSection}
          </div>
        </div>

        {partnerModals}
      </PageFrame>
    );
  }

  const showReceiptUploadInCard = customerMode
    ? true
    : showPartnerReceiptUpload(row);
  const showManualReceiptAmountInCard = showPartnerManualReceiptAmount(row);
  const latestUploadPath = row.latestUploadPath;

  return (
    <PageFrame
      backLink={{ ...listBackLink, className: "-ml-2 w-fit" }}
      className="gap-4 pb-6 sm:gap-5"
    >
      <BookingDetailHeader
        title={row.activityName || t("unknownActivity")}
        customerName={row.customerDisplayName}
        badge={
          <BookingStatusBadge
            status={row.status}
            handshakeStatus={row.handshakeStatus}
            size="lg"
          />
        }
        meta={row.serviceName ? [{ icon: ShoppingBag, label: row.serviceName }] : []}
      />

      <div
        className={cn(
          "grid gap-4 sm:gap-5",
          showHandshakeSection && "lg:grid-cols-3",
        )}
      >
        {showHandshakeSection ? (
          row.handshakeSessionId ? (
            <div className="order-2 min-w-0 lg:col-span-2">
              <Card className="flex min-h-0 flex-col overflow-hidden p-0">
                <BookingHandshakePanel
                  sessionId={row.handshakeSessionId}
                  adminMode={false}
                  canInteract={canInteractHandshake}
                  canSendMessage={canSendHandshakeMessage}
                />
              </Card>
            </div>
          ) : (
            <div className="order-1 lg:col-span-2">
              <EmptyState
                variant="dashed"
                size="sm"
                showIcon={false}
                title={t("detail.noHandshake")}
              />
            </div>
          )
        ) : null}

        <div
          className={cn(
            "order-1 space-y-4 sm:space-y-5",
            showHandshakeSection && "lg:col-span-1",
          )}
        >
          <SectionCard title={t("detail.orderInfo")} icon={ClipboardList}>
              <BookingDetailInfo
                row={row}
                booking={booking}
                viewer={
                  customerMode
                    ? "customer"
                    : sellerMode || hostMode
                      ? "partner"
                      : "staff"
                }
              />
          </SectionCard>

          <SectionCard title={t("detail.receipt")} icon={Receipt}>
              {row.receiptTotalCents != null ? (
                <ReceiptDisplay
                  receiptTotalCents={row.receiptTotalCents}
                  receiptUrl={row.receiptUrl}
                />
              ) : showManualReceiptAmountInCard && latestUploadPath ? (
                <div className="space-y-3">
                  <p className="text-sm text-warning-800">
                    {t("receiptManualRequiredMessage")}
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      receiptFlow.openManualAmount(row.id, latestUploadPath)
                    }
                  >
                    {t("receiptManualRequiredTitle")}
                  </Button>
                </div>
              ) : showReceiptUploadInCard ? (
                <ReceiptUploadButton
                  bookingId={row.id}
                  isProcessing={isProcessingReceipt}
                  fileInputResetKey={fileInputResetKey}
                  visitAt={row.visitAt}
                  onFileSelect={(file) =>
                    void receiptFlow.uploadReceipt(row, file)
                  }
                />
              ) : null}
          </SectionCard>
        </div>
      </div>

      {partnerModals}
    </PageFrame>
  );
}
